import { computed, type Ref } from 'vue';
import getAccountShipmentV2 from '@gpx-spa/queries/src/getAccountShipmentV2';
import getAccountCurrentPositionsV2 from '@gpx-spa/queries/src/getAccountCurrentPositionsV2';
import { displayStatusForRow } from '@/types/shipments';
import { SHIPMENT_DISPLAY_STATUS } from '@/utils/shipment-status-ui';
import {
  getShipmentEditCapabilities,
  type ShipmentEditCapabilities,
} from '@/components/shipment/edit/utils/shipment-edit-capabilities';
import { isDraftShipment } from '@/utils/shipment-draft-access';
import { currentPositionToLatLng } from '@/components/shipment/form/utils/current-position-to-lat-lng';
import type { LatLng } from '@/components/shipment/form/utils/build-routing-coordinates';

export function useShipmentEditCapabilitiesContext(options: {
  accountId: Ref<number>;
  shipmentId: Ref<number>;
}) {
  const shipmentQuery = getAccountShipmentV2(
    options.accountId,
    options.shipmentId,
  );
  const shipment = computed(() => shipmentQuery.data.value ?? null);

  const deviceIds = computed(() => shipment.value?.device_ids ?? []);

  const positionsQuery = getAccountCurrentPositionsV2(
    options.accountId,
    deviceIds,
    {
      enabled: computed(() => deviceIds.value.length > 0),
      refetchInterval: 60_000,
    },
  );

  /** All known positions for convoy map markers (order follows device_ids). */
  const trackerPositions = computed<LatLng[]>(() => {
    const rows = positionsQuery.data.value ?? [];
    const out: LatLng[] = [];
    for (const deviceId of deviceIds.value) {
      const row = rows.find((r) => r.device_id === deviceId);
      const ll = currentPositionToLatLng(row);
      if (ll) out.push(ll);
    }
    // Include any extra rows not in device_ids (should be rare).
    for (const row of rows) {
      if (deviceIds.value.includes(row.device_id)) continue;
      const ll = currentPositionToLatLng(row);
      if (ll) out.push(ll);
    }
    return out;
  });

  /**
   * First assigned tracker position — shared-route origin (ENG-2710 / convoy
   * shared corridor). Not a single-device API; routing uses one origin.
   */
  const primaryTrackerPosition = computed<LatLng | null>(() => {
    const rows = positionsQuery.data.value ?? [];
    const deviceId = deviceIds.value[0];
    const row =
      deviceId != null
        ? (rows.find((r) => r.device_id === deviceId) ?? rows[0])
        : rows[0];
    return currentPositionToLatLng(row);
  });

  const trackerLoading = computed(
    () =>
      deviceIds.value.length > 0 &&
      (positionsQuery.isLoading.value || !positionsQuery.isFetched.value),
  );

  const displayStatus = computed(() => {
    if (!shipment.value) return null;
    return displayStatusForRow(
      shipment.value as Parameters<typeof displayStatusForRow>[0],
    );
  });

  function isEditableDraftShipment(): boolean {
    const row = shipment.value;
    if (!row) return false;
    if (isDraftShipment(row)) return true;
    return displayStatus.value === SHIPMENT_DISPLAY_STATUS.Draft;
  }

  const capabilities = computed<ShipmentEditCapabilities>(() => {
    if (isEditableDraftShipment()) {
      return getShipmentEditCapabilities(SHIPMENT_DISPLAY_STATUS.Draft);
    }
    return getShipmentEditCapabilities(displayStatus.value ?? 'Closed');
  });

  const canEditTracker = computed(
    () =>
      isEditableDraftShipment() || capabilities.value.canAssignTracker,
  );

  const routingStrategy = computed(() => capabilities.value.routingStrategy);

  return {
    shipment,
    shipmentQuery,
    deviceIds,
    displayStatus,
    capabilities,
    canEditTracker,
    routingStrategy,
    primaryTrackerPosition,
    trackerPositions,
    trackerLoading,
    isEditableDraftShipment,
  };
}
